import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import TechnicalResponsibleModal from '../../components/Professional/TechnicalResponsibleModal';
import TechnicalResponsibleTable from '../../components/Professional/TechnicalResponsibleTable';
import TechnicalResponsibleFilters from '../../components/Professional/TechnicalResponsibleFilters';
import { professionalService, identityService } from '../../services/api';
import { unwrapList } from '../../utils/entityIds';
import { fetchAllPages } from '../../utils/apiPagination';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

const TechnicalResponsibles = () => {
  const { t } = useTranslation('professional');

  const [showModal, setShowModal] = useState(false);
  const [editingResponsible, setEditingResponsible] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    council: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const { useList, useCreate, useUpdate, useDelete } = useCrud(
    'technical-responsibles',
    professionalService.technicalResponsibleCrud
  );
  const councilCrud = useCrud('professional-councils', professionalService.professionalCouncilCrud);
  const { data: listData, isLoading, error } = useList();
  const { data: councilsPayload } = councilCrud.useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();
  const { data: persons = [] } = useQuery('professional-persons', () =>
    fetchAllPages((params) => identityService.getPersons(params))
  );

  const raw = unwrapList(listData);
  const councils = unwrapList(councilsPayload);
  const personName = useMemo(() => {
    const map = {};
    for (const person of persons) map[person.id] = person.name || person.id;
    return map;
  }, [persons]);
  const councilName = useMemo(() => {
    const map = {};
    for (const council of councils) map[council.id] = council.acronym || council.name || council.id;
    return map;
  }, [councils]);

  const responsibles = raw.map((row) => ({
    ...row,
    name: personName[row.person_id] || row.person_id || '—',
    registration_number: row.professional_license_number || '—',
    council: councilName[row.professional_council_id] || '—',
    status: row.is_active === false ? 'INACTIVE' : 'ACTIVE',
  }));

  const handleCreate = () => {
    setEditingResponsible(null);
    setShowModal(true);
  };

  const handleEdit = (responsible) => {
    setEditingResponsible(responsible);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este responsável técnico?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleSubmit = async (data) => {
    const payload = {
      person_id: data.person_id,
      professional_council_id: data.professional_council_id,
      professional_license_number: data.professional_license_number,
      is_active: data.status !== 'INACTIVE',
    };
    if (editingResponsible) {
      await updateMutation.mutateAsync({ id: editingResponsible.id, data: payload });
    } else {
      await createMutation.mutateAsync({
        person_id: payload.person_id,
        professional_council_id: payload.professional_council_id,
        professional_license_number: payload.professional_license_number,
      });
    }
    setShowModal(false);
    setEditingResponsible(null);
  };

  const filteredResponsibles = responsibles.filter((responsible) => {
    const matchesSearch = !filters.search ||
      String(responsible.name).toLowerCase().includes(filters.search.toLowerCase()) ||
      String(responsible.registration_number).includes(filters.search);
    const matchesStatus = !filters.status || responsible.status === filters.status;
    const matchesCouncil = !filters.council || responsible.council === filters.council;
    return matchesSearch && matchesStatus && matchesCouncil;
  });

  const sortedResponsibles = [...filteredResponsibles].sort((a, b) => {
    const aValue = String(a[filters.sortBy] || '');
    const bValue = String(b[filters.sortBy] || '');
    if (filters.sortOrder === 'asc') {
      return aValue.localeCompare(bValue);
    }
    return bValue.localeCompare(aValue);
  });

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("technicalTitle")}
          description="Profissionais habilitados com registro em conselho de classe."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Profissionais', to: '/professional/technical-responsibles' },
            { label: 'Responsáveis' },
          ]}
        >
          <Can permission={P.PROFESSIONAL_ACCESS}>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Novo responsável
            </button>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <TechnicalResponsibleFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          <div className="p-5 sm:p-6">
            <TechnicalResponsibleTable
              responsibles={sortedResponsibles}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <TechnicalResponsibleModal
          responsible={editingResponsible}
          persons={persons}
          councils={councils}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingResponsible(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default TechnicalResponsibles;
